import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Visible label text (also wires up the accessible name). */
  label: string;
  /** Inline error message shown below the field. */
  error?: string;
  /** Optional element rendered inside the field, right-aligned (e.g. a toggle). */
  trailing?: ReactNode;
}

/**
 * Labelled text input following the Kura Payments form style. The label is
 * associated with the input via `htmlFor`/`id`, so the field is reachable by its
 * label text; the error is wired with `aria-invalid` + `aria-describedby`.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, id, error, trailing, className = '', ...rest },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const errorId = `${inputId}-error`;

  return (
    <div>
      <label htmlFor={inputId} className="block text-sm font-medium text-[var(--color-text)] mb-2">
        {label}
      </label>
      <div className="relative">
        <input
          id={inputId}
          ref={ref}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          className={[
            // 16px below `sm`: iOS Safari zooms into any field smaller than that and
            // leaves the page zoomed after blur; 14px is fine on desktop.
            'py-2 px-3 block w-full min-h-[40px] border rounded-lg text-base sm:text-sm text-[var(--color-text)] placeholder-[var(--color-text-muted)] focus:ring-1 transition-colors',
            error
              ? 'border-[var(--color-danger)] focus:border-[var(--color-danger)] focus:ring-[var(--color-danger)]'
              : 'border-[var(--color-border-control)] focus:border-[var(--color-accent)] focus:ring-[var(--color-accent)]',
            trailing ? 'pr-12' : '',
            className,
          ]
            .filter(Boolean)
            .join(' ')}
          {...rest}
        />
        {trailing && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-1.5">{trailing}</div>
        )}
      </div>
      {error && (
        <p id={errorId} className="mt-1.5 text-xs text-[var(--color-danger)]">
          {error}
        </p>
      )}
    </div>
  );
});
